<template>
  <div class="journey-stats">
    <page-hero
        class="mb-1"
        :title="msg('journeystats')"
        :subtitle="msg('journeystatsintro')"
        variant="primary"
        :stats="[]"
    />

    <b-card no-body class="mb-1">
      <div class="js-filters">
        <v-select
            append-to-body class="js-filters__sel"
            v-model="journeyId" :options="journeys" label="name"
            :reduce="journey => journey.id" :placeholder="msg('alljourneys')"
            :dir="isRTL ? 'rtl' : 'ltr'" @input="refresh"
        />
        <b-button variant="flat-secondary" size="sm" @click="refresh">
          <feather-icon icon="RefreshCwIcon" size="14"/>
        </b-button>
      </div>
    </b-card>

    <div v-if="loading" class="text-center text-primary my-3">
      <b-spinner class="align-middle mx-1"/>
      <strong>{{ msg('Loading') }}...</strong>
    </div>

    <template v-else>
      <!-- overview -->
      <b-row class="mb-1">
        <b-col cols="6" md="3" v-for="tile in tiles" :key="tile.label">
          <b-card no-body class="js-tile">
            <div class="js-tile__icon" :style="{background: tile.bg, color: tile.color}">
              <feather-icon :icon="tile.icon" size="19"/>
            </div>
            <div>
              <div class="js-tile__value">{{ tile.value }}</div>
              <div class="js-tile__label">{{ tile.label }}</div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <div v-if="questions.length === 0" class="js-empty">
        <feather-icon icon="BarChart2Icon" size="30" class="mb-50"/>
        <div>{{ msg('noresponsesyet') }}</div>
      </div>

      <!-- one card per question -->
      <b-row>
        <b-col v-for="question in questions" :key="question.questionKey" cols="12" md="6">
          <b-card no-body class="js-q">
            <div class="js-q__head">
              <span class="js-q__label">{{ question.label || question.questionKey }}</span>
              <small class="js-q__meta">
                {{ question.answered }} {{ msg('answeredby') }}
                <template v-if="question.skipped"> · {{ question.skipped }} {{ msg('skipped') }}</template>
              </small>
            </div>

            <div class="js-q__body">
              <!-- option distribution -->
              <template v-if="(question.options || []).length">
                <div v-for="option in question.options" :key="option.value" class="js-bar">
                  <div class="js-bar__row">
                    <span class="js-bar__label">{{ option.label || option.value }}</span>
                    <span class="js-bar__count">{{ option.count }} · {{ option.percent }}%</span>
                  </div>
                  <div class="js-bar__track"><span :style="{width: option.percent + '%'}"></span></div>
                </div>
              </template>

              <!-- numeric summary -->
              <div v-else-if="question.average != null" class="js-numbers">
                <div><span>{{ msg('averagerating') }}</span><b>{{ question.average }}</b></div>
                <div><span>{{ msg('min') }}</span><b>{{ question.min }}</b></div>
                <div><span>{{ msg('max') }}</span><b>{{ question.max }}</b></div>
              </div>

              <!-- free text samples -->
              <div v-else-if="(question.topTexts || []).length" class="js-texts">
                <div v-for="(text, index) in question.topTexts" :key="index" class="js-texts__item">
                  “{{ text }}”
                </div>
              </div>

              <div v-else class="text-muted">{{ msg('noanswers') }}</div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import PageHero from '@/components/PageHero'

export default {
  name: 'JourneyStats',
  components: {PageHero},
  data() {
    return {
      loading: true,
      journeys: [],
      journeyId: null,
      overview: {},
      questions: [],
      listeners: {refresh: () => {}},
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('appConfig', ['isRTL']),
    tiles() {
      const overview = this.overview || {};
      return [
        {
          label: this.msg('responses'), value: overview.total || 0, icon: 'InboxIcon',
          bg: 'rgba(115,103,240,.12)', color: '#7367f0'
        },
        {
          label: this.msg('completionrate'), value: (overview.completionRate || 0) + '%', icon: 'CheckCircleIcon',
          bg: 'rgba(40,199,111,.12)', color: '#28c76f'
        },
        {
          label: this.msg('upsellrevenue'),
          value: (overview.upsellRevenue || 0) + ' ' + (overview.currency || '€'),
          icon: 'DollarSignIcon', bg: 'rgba(255,159,67,.12)', color: '#ff9f43'
        },
        {
          label: this.msg('itemssold'), value: overview.itemsSold || 0, icon: 'ShoppingBagIcon',
          bg: 'rgba(0,207,232,.12)', color: '#00cfe8'
        },
      ];
    },
  },
  created() {
    this.loadJourneys();
    this.refresh();
    this.listeners.refresh = () => this.refresh();
    this.$root.$on('crm::branch-updated', this.listeners.refresh);
  },
  beforeDestroy() {
    this.$root.$off('crm::branch-updated', this.listeners.refresh);
  },
  methods: {
    refresh() {
      const $this = this;
      this.loading = true;
      this.$store.dispatch('journey/getStats', {journeyId: this.journeyId})
          .then(ans => {
            $this.overview = ans['Overview'] || {};
            $this.questions = ans['Questions'] || [];
          })
          .finally(() => $this.loading = false);
    },
    loadJourneys() {
      const $this = this;
      this.$store.dispatch('journey/getAll', {amount: 200})
          .then(ans => $this.journeys = ans['Journeys'] || [])
          .catch(() => {
          });
    },
  }
}
</script>

<style lang="scss" scoped>
.js-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;

  &__sel {
    min-width: 230px;
  }
}

.js-tile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 12px;
  }

  &__value {
    font-weight: 700;
    font-size: 18px;
    color: #5e5873;
  }

  &__label {
    color: #6e6b7b;
    font-size: 12.5px;
  }
}

.js-empty {
  text-align: center;
  color: #b9b9c3;
  padding: 3rem 1rem;
}

.js-q {
  padding: 14px 16px;

  &__head {
    margin-bottom: 10px;
  }

  &__label {
    font-weight: 700;
    color: #5e5873;
    display: block;
  }

  &__meta {
    color: #6e6b7b;
  }
}

.js-bar {
  margin-bottom: 8px;

  &__row {
    display: flex;
    justify-content: space-between;
    font-size: 12.5px;
    margin-bottom: 3px;
  }

  &__label {
    color: #5e5873;
  }

  &__count {
    color: #6e6b7b;
  }

  &__track {
    height: 7px;
    border-radius: 7px;
    background: rgba(34, 41, 47, .07);
    overflow: hidden;

    span {
      display: block;
      height: 100%;
      background: #7367f0;
    }
  }
}

.js-numbers {
  display: flex;
  gap: 18px;

  div {
    display: flex;
    flex-direction: column;
  }

  span {
    color: #6e6b7b;
    font-size: 12px;
  }

  b {
    font-size: 17px;
    color: #5e5873;
  }
}

.js-texts__item {
  padding: 5px 0;
  border-bottom: 1px dashed rgba(34, 41, 47, .08);
  color: #6e6b7b;
  font-style: italic;
}

.dark-layout {
  .js-tile__value, .js-q__label, .js-bar__label, .js-numbers b {
    color: #d0d2d6;
  }

  .js-tile__label, .js-q__meta, .js-bar__count, .js-texts__item {
    color: #b4b7bd;
  }

  .js-bar__track {
    background: rgba(255, 255, 255, .1);
  }
}
</style>
